import { useState, useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { Button } from 'ui-components/components/button';
import { Checkbox } from 'ui-components/components/checkbox';
import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { Popover } from 'ui-components/components/popover';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import AccountLocked from './account-locked';
import InputWithErrorTooltip from './input-with-error-tooltip';
import messages from './messages';
import styles from './styles.module.less';
import UserDateFormat from '../../../../../components/user-date-timezone-format';
import { emptyArray } from 'ui-components/default-props/empty';
import { generatePasswordResetLink } from '../../../../../services/password-reset-link-generator';
import Global from '../../../../../store/global-selectors';

const MAX_NAME_LENGTH = 50;

const Information = ({
  isEditMode,
  userId,
  username,
  name,
  email,
  userCreatedDate,
  lastLoggedInDate,
  isActive,
  isLoginAllowed,
  isLockedOut,
  refreshUserGrid,
  updateForm,
  formErrors,
  clearErrorForField,
  defaultInventoryWarehouseId,
}) => {
  const intl = useIntl();
  const headerMessage = isEditMode ? (
    <FormattedMessage {...messages.infoEditUserHeader} />
  ) : (
    <FormattedMessage {...messages.infoNewUserHeader} values={{ b: (chunks) => <b>{chunks}</b> }} />
  );

  // Inventory Warehouse dropdown functionality
  const inventoryWarehouses = useSelector(Global.selectSellerInventoryWarehouses);
  const activeWarehouses = inventoryWarehouses?.filter((warehouse) => warehouse.isActive) || emptyArray;

  // Compute selected warehouse name with memoization for better performance and readability
  const selectedWarehouseName = useMemo(() => {
    const warehouse = defaultInventoryWarehouseId
      ? activeWarehouses.find((w) => w.inventoryWarehouseId === defaultInventoryWarehouseId)
      : null;
    return warehouse?.name || intl.formatMessage(messages.selectWarehouse);
  }, [defaultInventoryWarehouseId, activeWarehouses, intl]);

  // Use the parent form's updateForm instead of local state
  const handleWarehouseChange = useCallback(
    (warehouseId) => {
      updateForm('defaultInventoryWarehouseId', warehouseId);
    },
    [updateForm],
  );

  // For fraud reduction, we need to prevent the
  // primary admin user from changing their email address.
  // Internal users (ShipStation employees) can change the
  // primary admin user's email address if needed.
  const sellerEmail = useSelector(Global.selectSellerEmail);
  const isSystemAdmin = useSelector(Global.selectUserisSysAdmin);
  const isPrimarySellerEmail = sellerEmail === email;
  const [showEmailWarning, setShowEmailWarning] = useState(false);
  const [overrideShowEmailWarning, setOverrideShowEmailWarning] = useState(false);
  const acknowledgeEmailWarning = useCallback(() => {
    setShowEmailWarning(false);
    setOverrideShowEmailWarning(true);
  }, [setShowEmailWarning, setOverrideShowEmailWarning]);

  const toggleEmailPopover = useCallback(() => {
    if (overrideShowEmailWarning) {
      return;
    }
    setShowEmailWarning((show) => !show);
  }, [overrideShowEmailWarning, setShowEmailWarning]);

  const onChange = useCallback(
    (field, value) => {
      clearErrorForField(field);
      updateForm(field, value);
    },
    [clearErrorForField, updateForm],
  );

  const emailWarningPopoverBody = (
    <div className={styles['email-warning']}>
      <div className={styles.header}>
        <FormattedMessage {...messages.headsUp} />
      </div>
      <div className={styles.body}>
        <div>
          <FormattedMessage {...messages.emailWarning} values={{ b: (chunks) => <b>{chunks}</b> }} />
        </div>
        <Button onClick={acknowledgeEmailWarning} theme={THEME_CONSTANTS.tertiary}>
          <FormattedMessage {...messages.understand} />
        </Button>
      </div>
    </div>
  );

  const shouldShowChangePasswordField = isEditMode;
  const passwordResetLink = generatePasswordResetLink(username);

  return (
    <div className={styles.information}>
      <div className={styles.header}>{headerMessage}</div>
      <div className={styles.body}>
        <div className={styles.main}>
          <div className={styles['left-section']}>
            <InputWithErrorTooltip
              disabled={isEditMode}
              errorDescriptor={formErrors.username?.failReason}
              hasError={!isEditMode && !!formErrors.username}
              id='username'
              labelDescriptor={messages.username}
              onChange={onChange}
              value={username}
            />
            {shouldShowChangePasswordField ? (
              <>
                <FormattedMessage {...messages.password} />
                <Button href={passwordResetLink} target='_self' theme={THEME_CONSTANTS.primary}>
                  <FormattedMessage {...messages.changePassword} />
                </Button>
              </>
            ) : null}
            <InputWithErrorTooltip
              errorDescriptor={formErrors.name?.failReason}
              hasError={!!formErrors.name}
              id='name'
              labelDescriptor={messages.name}
              maxLength={MAX_NAME_LENGTH}
              onChange={onChange}
              value={name}
            />
            <Popover
              body={emailWarningPopoverBody}
              enterExitTransitionDurationMs={0}
              isOpen={isEditMode && !overrideShowEmailWarning ? showEmailWarning : null}
              onOuterAction={toggleEmailPopover}
              preferPlace='below'
            >
              <div>
                <FormattedMessage {...messages.email} />
              </div>
            </Popover>
            <InputWithErrorTooltip
              disabled={isPrimarySellerEmail ? !isSystemAdmin : null}
              disabledTooltipMessage={messages.pleaseContactSupport}
              errorDescriptor={formErrors.email?.failReason}
              hasError={!!formErrors.email}
              id='email'
              onChange={onChange}
              onFocus={toggleEmailPopover}
              value={email}
            />
            {activeWarehouses.length > 0 && (
              <>
                <label htmlFor='defaultInventoryWarehouse'>
                  <FormattedMessage {...messages.inventoryWarehouse} />
                </label>
                <DropdownMenu dropdownMenuShouldHaveWidthOfButton fullWidth id='defaultInventoryWarehouse' variant='form'>
                  <DropdownMenuToggler>{selectedWarehouseName}</DropdownMenuToggler>
                  <DropdownMenuItem key='' onClick={() => handleWarehouseChange(null)}>
                    <FormattedMessage {...messages.selectWarehouse} />
                  </DropdownMenuItem>
                  {activeWarehouses.map((warehouse) => (
                    <DropdownMenuItem
                      key={warehouse.inventoryWarehouseId}
                      onClick={() => handleWarehouseChange(warehouse.inventoryWarehouseId)}
                      selected={defaultInventoryWarehouseId === warehouse.inventoryWarehouseId}
                    >
                      {warehouse.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenu>
              </>
            )}
          </div>
          {isEditMode ? (
            <div className={styles['right-section']}>
              <div className={styles['date-data']}>
                <b>
                  <FormattedMessage {...messages.userCreated} />
                </b>
                <UserDateFormat dateTime={userCreatedDate} />
              </div>
              <div className={styles['date-data']}>
                <b>
                  <FormattedMessage {...messages.lastLoggedIn} />
                </b>
                <UserDateFormat dateTime={lastLoggedInDate} />
              </div>
              {isLockedOut ? <AccountLocked refreshUserGrid={refreshUserGrid} userId={userId} /> : null}
              {!isLockedOut && isActive ? (
                <b className={styles.ok}>
                  <FormattedMessage {...messages.isActive} />
                </b>
              ) : null}
              {!isLockedOut && !isActive && (
                <b className={styles.danger}>
                  <FormattedMessage {...messages.isInactive} />
                </b>
              )}
            </div>
          ) : null}
        </div>
        {isEditMode ? (
          <Checkbox
            checked={isLoginAllowed}
            label={<FormattedMessage {...messages.allowLogin} />}
            onChange={(value) => updateForm('isLoginAllowed', value)}
          />
        ) : null}
      </div>
    </div>
  );
};

export default Information;
